import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronDoubleUp, faRight } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { SplitProgressBar } from '@sorare/core/src/components/collections/SplitProgressBar';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { ensureHexColor, isLightColor } from '@sorare/core/src/lib/colors';

import { MissingCardsForCollection } from '../MissingCardsForCollection';
import type {
  LazyCollectionElement_CardCollectionScoreIncrease,
  LazyCollectionUpsellElementQuery,
  LazyCollectionUpsellElementQueryVariables,
} from './__generated__/index.graphql';

const Container = styled(Vertical)`
  padding: 0 var(--double-unit);
`;

const CollectionProgress = styled(Vertical)`
  padding: var(--intermediate-unit);
  border-radius: var(--unit);
  background-color: var(--background-color);
`;

const Shine = styled.span`
  text-shadow: 0px 1px 7px var(--c-yellow-400);
  filter: drop-shadow(0px 1px 7px var(--c-yellow-400));
`;

const LAZY_COLLECTION_UPSELL_ELEMENT_QUERY = gql`
  query LazyCollectionUpsellElementQuery($userCollectionId: String!) {
    userCardCollection(id: $userCollectionId) {
      id
      cardCollection {
        slug
        anyTeam {
          slug
          styleGuideColor
          ...TeamAvatar_team
        }
        ...SplitProgressBar_cardCollection
      }
      ...SplitProgressBar_userCardCollection
      ...MissingCardsForCollection_userCardCollection
    }
  }
  ${TeamAvatar.fragments.team}
  ${SplitProgressBar.fragments.cardCollection}
  ${SplitProgressBar.fragments.userCardCollection}
  ${MissingCardsForCollection.fragments.userCardCollection}
` as TypedDocumentNode<
  LazyCollectionUpsellElementQuery,
  LazyCollectionUpsellElementQueryVariables
>;

type Props = {
  cardCollectionScoreIncrease: LazyCollectionElement_CardCollectionScoreIncrease;
};

export const LazyCollectionElement = ({
  cardCollectionScoreIncrease,
}: Props) => {
  const userCollectionId =
    cardCollectionScoreIncrease?.userCardCollection?.id || '';

  const { data } = useQuery(LAZY_COLLECTION_UPSELL_ELEMENT_QUERY, {
    variables: {
      userCollectionId: idFromObject(userCollectionId),
    },
    skip: !userCollectionId,
  });

  if (!data) return null;

  const { userCardCollection } = data;

  const { styleGuideColor } = userCardCollection.cardCollection.anyTeam || {};
  const backgroundColor =
    styleGuideColor && !isLightColor(ensureHexColor(styleGuideColor))
      ? styleGuideColor
      : 'var(--c-nl-700-opaque)';
  const { bonus } = userCardCollection;

  //update with correct values from BE
  const scoreIncrease = 110;
  const bonusIncreased = 0.03;

  return (
    <>
      <Container>
        <CollectionProgress
          style={
            { '--background-color': backgroundColor } as React.CSSProperties
          }
        >
          <Horizontal>
            <TeamAvatar
              size={20}
              team={userCardCollection?.cardCollection.anyTeam}
            />
            <BodyM bold>
              {userCardCollection.score}{' '}
              <BodyM as="span" bold color="var(--c-yellow-400)">
                +{scoreIncrease} <Lightning />
              </BodyM>
            </BodyM>
            <Chip color="yellow" size="medium">
              <Horizontal as="span" gap={0.5}>
                <span>+{bonus * 100}%</span>
                {bonusIncreased > bonus && (
                  <>
                    <FontAwesomeIcon
                      icon={faRight}
                      color="var(--c-yellow-400)"
                    />
                    <Shine>+{bonusIncreased * 100}%</Shine>
                    <Shine>
                      <FontAwesomeIcon icon={faChevronDoubleUp} />
                    </Shine>
                  </>
                )}
              </Horizontal>
            </Chip>
          </Horizontal>
          <SplitProgressBar
            cardCollection={userCardCollection?.cardCollection}
            userCardCollection={userCardCollection}
            scoreIncrease={scoreIncrease}
            withoutLabel
          />
        </CollectionProgress>
      </Container>
      <MissingCardsForCollection userCardCollection={userCardCollection} />
    </>
  );
};

LazyCollectionElement.fragments = {
  cardCollectionScoreIncrease: gql`
    fragment LazyCollectionElement_CardCollectionScoreIncrease on CardCollectionScoreIncrease {
      scoreIncrease
      userCardCollection {
        id
      }
    }
  ` as TypedDocumentNode<LazyCollectionElement_CardCollectionScoreIncrease>,
};
